import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from "react";

import { Box, Stack } from "@mui/material";

import { useTranslate } from "../../../../../hooks/useTranslate";
import { MODAL_Z_INDEX } from "../constants";

import { DashboardAutocomplete } from "@/dashboard-design-system/Autocomplete";
import { DashboardCheckbox } from "@/dashboard-design-system/DashboardCheckbox";
import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { ChevronLeft, ChevronRight } from "@/icons/outlined";

export type CampaignScopeAutocompleteProps = {
  campaignOptions: {
    label: string;
    value: string;
  }[];
  selectedCampaigns: {
    label: string;
    value: string;
  }[];
  setSelectedCampaigns: Dispatch<
    SetStateAction<
      {
        label: string;
        value: string;
      }[]
    >
  >;
  campaignScopeOptions: {
    label: string;
    value: string;
  }[];
  selectedCampaignScope:
    | {
        label: string;
        value: string;
      }
    | undefined;
  setSelectedCampaignScope: Dispatch<
    SetStateAction<
      | {
          label: string;
          value: string;
        }
      | undefined
    >
  >;
  disabled?: boolean;
  isAllCampaignsDisabled?: boolean;
};

export const CampaignScopeAutocomplete: FC<CampaignScopeAutocompleteProps> = ({
  campaignOptions,
  selectedCampaigns,
  setSelectedCampaigns,
  campaignScopeOptions,
  selectedCampaignScope,
  setSelectedCampaignScope,
  disabled,
  isAllCampaignsDisabled,
}) => {
  const { t } = useTranslate();

  const hasCampaigns = useMemo(() => selectedCampaigns.length > 0, [selectedCampaigns]);

  const campaignOptionsWithBack = isAllCampaignsDisabled
    ? campaignOptions
    : [{ label: t("common", "back"), value: "back" }, ...campaignOptions];

  const [isSelectingCampaigns, setIsSelectingCampaigns] = useState(hasCampaigns);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Synchronize isSelectingCampaigns when selectedCampaigns changes
  useEffect(() => {
    if (hasCampaigns && !isSelectingCampaigns) {
      setIsSelectingCampaigns(true);
      // Set selectedCampaignScope to "specific" when there are campaigns
      const specificOption = campaignScopeOptions.find((option) => option.value === "specific");
      if (specificOption && selectedCampaignScope?.value !== "specific") {
        setSelectedCampaignScope(specificOption);
      }
    }
  }, [hasCampaigns, isSelectingCampaigns, campaignScopeOptions, selectedCampaignScope, setSelectedCampaignScope]);

  const handleAutocompleteChange = (newValue: unknown) => {
    type OptionType = { label: string; value: string };

    if (!newValue) {
      return;
    }

    if (Array.isArray(newValue)) {
      if (newValue.length === 0) {
        setSelectedCampaigns([]);
      } else if (typeof newValue[0] === "object" && "value" in newValue[0]) {
        const typedValues = newValue as OptionType[];
        // Filter "Back" option if it exists
        const filteredCampaigns = typedValues.filter((item) => item);
        setSelectedCampaigns(filteredCampaigns);
      }
      return;
    }

    if (typeof newValue === "object" && "value" in newValue) {
      const typedValue = newValue as OptionType;
      if (typedValue.value === "specific") {
        setIsSelectingCampaigns(true);
        setSelectedCampaignScope(typedValue);
      } else if (typedValue.value === "all") {
        setSelectedCampaignScope(typedValue);
        setSelectedCampaigns([]);
        setIsMenuOpen(false);
      }
    }
  };

  useEffect(() => {
    if (isAllCampaignsDisabled) {
      setIsSelectingCampaigns(true);
    }
  }, [isAllCampaignsDisabled]);

  return (
    <DashboardAutocomplete
      data-test="campaign-scope-autocomplete"
      options={isSelectingCampaigns ? campaignOptionsWithBack : campaignScopeOptions}
      value={isSelectingCampaigns ? selectedCampaigns : (selectedCampaignScope ?? null)}
      onChange={handleAutocompleteChange}
      open={isMenuOpen}
      onOpen={() => setIsMenuOpen(true)}
      onClose={() => setIsMenuOpen(false)}
      multiple={isSelectingCampaigns}
      key={isSelectingCampaigns ? "multiple" : "single"}
      placeholder={t("dashboard", "userPermissionModal.campaignScopeAutocomplete.placeholder")}
      disableClearable={!isSelectingCampaigns}
      disabled={disabled}
      renderOption={(props, option, { selected }) => {
        if (isSelectingCampaigns) {
          if (typeof option === "object" && "value" in option && option.value === "back" && !isAllCampaignsDisabled) {
            return (
              <li
                {...props}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsSelectingCampaigns(false);
                  setSelectedCampaignScope(campaignScopeOptions[1]);
                  setSelectedCampaigns([]);
                }}
              >
                <Stack direction="row" gap={1} alignItems="center">
                  <DashboardIcon size="small">
                    <ChevronLeft />
                  </DashboardIcon>
                  <Box>{option.label}</Box>
                </Stack>
              </li>
            );
          }
          return (
            <li {...props}>
              <DashboardCheckbox checked={selected} style={{ marginRight: 8 }} size="small" />
              {typeof option === "object" ? option.label : option}
            </li>
          );
        }

        // Select campaign scope mode - display ChevronRight for "specific"
        if (typeof option === "object" && "value" in option && option.value === "specific") {
          return (
            <li {...props}>
              <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                <Box>{option.label}</Box>
                <DashboardIcon size="small">
                  <ChevronRight />
                </DashboardIcon>
              </Stack>
            </li>
          );
        }

        // Default option "all" mode
        return <li {...props}>{typeof option === "object" ? option.label : option}</li>;
      }}
      slotProps={{
        popper: {
          sx: {
            zIndex: MODAL_Z_INDEX + 1,
          },
        },
        paper: {
          sx: {
            "& .MuiAutocomplete-listbox": {
              maxHeight: 160,
            },
          },
        },
      }}
    />
  );
};
